/**
 * Doporučení aplikace: tlačítko pro sdílení odkazu a leták A5 s QR kódem k tisku.
 */
import { useEffect, useRef, useState } from 'react';
import { qrSvg } from '../lib/qr';
import { go } from './hooks';
import { Icon } from './icons';
import { Screen, Section, toast } from './ui';

/** Adresa aplikace (bez #…) – po případném přechodu na vlastní doménu se změní sama. */
export const appUrl = () => location.href.split('#')[0].replace(/index\.html$/, '');

const shareText = (url: string) =>
  'Zkus Úlník – aplikaci pro včelaře na evidenci prohlídek, léčení, krmení a medobraní. ' +
  'Funguje i bez signálu u úlů a první sezónu je zdarma.\n' +
  `${url}\n\n` +
  'iPhone: otevři odkaz v Safari → Sdílet (čtvereček se šipkou) → Přidat na plochu.\n' +
  'Android: otevři v Chrome → menu ⋮ → Instalovat aplikaci.\n' +
  'Aplikaci pak spouštěj z ikony na ploše.';

export async function shareApp() {
  const url = appUrl();
  const text = shareText(url);
  if (navigator.share) {
    try {
      // URL je i v textu – některé aplikace (SMS) pole url zahodí
      await navigator.share({ title: 'Úlník – včelařská evidence', text });
      return;
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') return;
    }
  }
  try {
    await navigator.clipboard.writeText(text);
    toast('Text s odkazem zkopírován – vložte ho do zprávy.');
  } catch {
    toast(url);
  }
}

export function RecommendSetting() {
  return (
    <Section title="Doporučit Úlník">
      <p className="muted">Pošlete aplikaci kamarádům včelařům, nebo si vytiskněte leták s QR kódem třeba na schůzi spolku.</p>
      <div className="quick-row">
        <button className="btn primary" onClick={() => void shareApp()}>
          <Icon name="mail" size={20} /> Poslat odkaz
        </button>
        <button className="btn" onClick={() => go('letak')}>
          <Icon name="print" size={20} /> Leták s QR kódem
        </button>
      </div>
    </Section>
  );
}

const FEATURES: [string, string][] = [
  ['Zápis pár ťuknutími', 'prohlídky, léčení, krmení, varroáza, medobraní'],
  ['Funguje bez signálu', 'přímo u úlů, data zůstávají jen ve vašem telefonu'],
  ['Evidence léčiv a hlášení', 'připravené k tisku, export do Excelu a PDF'],
  ['QR štítky na úly', 'načtete telefonem a hned zapisujete'],
  ['Přehledy a grafy', 'spad roztočů, zakrmení, zimování, výroční zpráva'],
];

/** Samotný leták – na obrazovce jako náhled, při tisku přesně A5. */
export function Flyer() {
  const url = appUrl();
  const shown = url.replace(/^https?:\/\//, '').replace(/\/$/, '');
  return (
    <article className="flyer">
      <header className="flyer-head">
        <img src="icons/icon.svg" alt="" width={84} height={84} />
        <div>
          <h2>Úlník</h2>
          <p>včelařská evidence v mobilu</p>
        </div>
      </header>
      <ul className="flyer-features">
        {FEATURES.map(([t, d]) => (
          <li key={t}>
            <strong>{t}</strong>
            <span>{d}</span>
          </li>
        ))}
      </ul>
      <div className="flyer-qr">
        <div className="qr" dangerouslySetInnerHTML={{ __html: qrSvg(url, 'M') }} />
        <div className="flyer-how">
          <p className="flyer-scan">Načtěte QR kód fotoaparátem telefonu</p>
          <p className="flyer-url">{shown}</p>
          <p>Otevře se Úlník – uložte si ho na plochu telefonu jako aplikaci.</p>
          <p className="flyer-turn">Návod krok za krokem je na druhé straně →</p>
        </div>
      </div>
      <footer className="flyer-foot">
        Žádná registrace ani e‑mail · data zůstávají jen ve vašem telefonu
        <br />
        Zkušební verze zdarma, bez záruky – pravidelně zálohujte.
      </footer>
    </article>
  );
}

// Obrázky tlačítek, na která se při přidání na plochu ťuká ------------------------

const G = { fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' } as const;
export const ShareGlyph = () => (
  <svg viewBox="0 0 24 24" className="glyph" aria-label="Sdílet">
    <path {...G} d="M12 3v12M8 7l4-4 4 4M7 10H5v11h14V10h-2" />
  </svg>
);
export const AddGlyph = () => (
  <svg viewBox="0 0 24 24" className="glyph" aria-label="Přidat na plochu">
    <rect {...G} x="4" y="4" width="16" height="16" rx="3" />
    <path {...G} d="M12 8v8M8 12h8" />
  </svg>
);
export const DotsGlyph = () => (
  <svg viewBox="0 0 24 24" className="glyph" aria-label="Menu">
    <circle cx="12" cy="5" r="2" fill="currentColor" />
    <circle cx="12" cy="12" r="2" fill="currentColor" />
    <circle cx="12" cy="19" r="2" fill="currentColor" />
  </svg>
);

/** Kroky přidání na plochu – leták (2. strana) i upozornění v aplikaci. */
export function InstallSteps({ only }: { only?: 'ios' | 'android' }) {
  return (
    <div className="install-steps">
      {only !== 'android' && (
        <section>
          <h3>iPhone a iPad (Safari)</h3>
          <ol>
            <li>
              Otevřete odkaz v prohlížeči <strong>Safari</strong> (ne v Messengeru, WhatsAppu ani Facebooku).
            </li>
            <li>
              Ťukněte na tlačítko <strong>Sdílet</strong> <ShareGlyph /> – dole uprostřed, nebo vedle adresy.
            </li>
            <li>
              Sjeďte níž a zvolte <strong>Přidat na plochu</strong> <AddGlyph />.
            </li>
            <li>
              Vpravo nahoře ťukněte na <strong>Přidat</strong>.
            </li>
          </ol>
        </section>
      )}
      {only !== 'ios' && (
        <section>
          <h3>Android (Chrome)</h3>
          <ol>
            <li>
              Otevřete odkaz v prohlížeči <strong>Chrome</strong>.
            </li>
            <li>
              Vpravo nahoře ťukněte na menu <DotsGlyph /> (tři tečky).
            </li>
            <li>
              Zvolte <strong>Instalovat aplikaci</strong> nebo <strong>Přidat na plochu</strong>.
            </li>
            <li>
              Potvrďte <strong>Instalovat</strong>.
            </li>
          </ol>
        </section>
      )}
    </div>
  );
}

/** Druhá strana letáku: návod, jak si Úlník uložit na plochu. */
export function FlyerBack() {
  return (
    <article className="flyer flyer-back">
      <h2>Jak dostat Úlník na plochu telefonu</h2>
      <p className="flyer-intro">
        Úlník se nestahuje z App Store ani Google Play. Otevřete ho v prohlížeči a <strong>uložte na plochu</strong> – pak má vlastní ikonu,
        otevírá se přes celou obrazovku a funguje i bez signálu, <strong>úplně jako běžná aplikace</strong>.
      </p>
      <InstallSteps />
      <div className="flyer-result">
        <figure>
          <img src="icons/icon.svg" alt="" width={64} height={64} />
          <figcaption>Úlník</figcaption>
        </figure>
        <p>
          <strong>Hotovo – na ploše uvidíte tuto ikonu.</strong> Úlník spouštějte vždy z ní, ne z prohlížeče: data se ukládají k ikoně.
        </p>
      </div>
    </article>
  );
}

/** Náhled zmenšený na šířku obrazovky (leták je navržený v milimetrech pro A5). */
function FlyerPreview() {
  const ref = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState(1);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const fit = () => setZoom(Math.min(1, el.clientWidth / ((148 / 25.4) * 96)));
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return (
    <div className="flyer-wrap" ref={ref}>
      <div className="flyer-zoom" style={{ zoom }}>
        <Flyer />
        <FlyerBack />
      </div>
    </div>
  );
}

export function FlyerScreen() {
  // Tisk přesně na A5 bez okrajů – platí jen, dokud je otevřená tato obrazovka.
  useEffect(() => {
    const st = document.createElement('style');
    st.textContent = '@page { size: A5 portrait; margin: 0; }';
    document.head.appendChild(st);
    return () => st.remove();
  }, []);
  return (
    <Screen title="Leták s QR kódem" crumbs={[['Nastavení', 'nastaveni']]} backTo="nastaveni">
      <p className="muted no-print">
        Leták má velikost A5 a dvě strany: na přední je QR kód, na zadní návod, jak si Úlník uložit na plochu. Tiskněte oboustranně na A5,
        nebo na A4 s volbou „Přizpůsobit stránce“. Přes Tisk lze leták uložit i jako PDF.
      </p>
      <div className="quick-row no-print">
        <button className="btn primary" onClick={() => window.print()}>
          <Icon name="print" size={20} /> Tisk / PDF
        </button>
        <button className="btn" onClick={() => void shareApp()}>
          <Icon name="mail" size={20} /> Poslat odkaz
        </button>
      </div>
      <FlyerPreview />
    </Screen>
  );
}

// ---------------------------------------------------------------------------
// Upozornění v aplikaci otevřené jen v prohlížeči: „Přidejte si Úlník na plochu“
// ---------------------------------------------------------------------------

interface InstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}
let installEvent: InstallPromptEvent | null = null;
const installListeners = new Set<() => void>();

/** Android/Chrome nabízí instalaci událostí, která přijde hned po startu – zachytíme ji. */
export function installA2HS() {
  addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    installEvent = e as InstallPromptEvent;
    installListeners.forEach((l) => l());
  });
  addEventListener('appinstalled', () => {
    installEvent = null;
    installListeners.forEach((l) => l());
  });
}

export const isStandalone = () =>
  matchMedia('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
export const platform = (): 'ios' | 'android' | null => {
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)) return 'ios';
  if (/Android/.test(ua)) return 'android';
  return null;
};
const HINT_KEY = 'ulnik-install-hint-hidden';

export function InstallHint() {
  const [, rerender] = useState(0);
  const [hidden, setHidden] = useState(() => {
    try {
      return localStorage.getItem(HINT_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const l = () => rerender((n) => n + 1);
    installListeners.add(l);
    return () => void installListeners.delete(l);
  }, []);
  const os = platform();
  if (hidden || isStandalone() || !os) return null;
  const hide = () => {
    setHidden(true);
    try {
      localStorage.setItem(HINT_KEY, '1');
    } catch {
      /* nevadí */
    }
  };
  return (
    <div className="notice warn install-hint">
      <p>
        <strong>Přidejte si Úlník na plochu telefonu.</strong> Teď ho máte otevřený jen v prohlížeči. Z ikony na ploše se otevírá jako běžná
        aplikace, přes celou obrazovku a i bez signálu.
      </p>
      <div className="quick-row">
        {installEvent ? (
          <button
            className="btn primary"
            onClick={async () => {
              const ev = installEvent!;
              await ev.prompt();
              if ((await ev.userChoice).outcome === 'accepted') hide();
            }}
          >
            Instalovat Úlník
          </button>
        ) : (
          <button className="btn primary" onClick={() => setOpen(!open)}>
            {open ? 'Skrýt návod' : 'Jak na to'}
          </button>
        )}
        <button className="btn link" onClick={hide}>
          Už nezobrazovat
        </button>
      </div>
      {open && !installEvent && <InstallSteps only={os} />}
    </div>
  );
}
